'use client';

import { useState, useEffect, type ReactNode } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

type AnimationType = 'fade-up' | 'fade-in' | 'slide-left' | 'slide-right';

interface ScrollRevealProps {
  children: ReactNode;
  /** Animation type. Default: 'fade-up' */
  animation?: AnimationType;
  /** Delay in ms for staggering. Default: 0 */
  delay?: number;
  /** Duration in ms. Default: 600 */
  duration?: number;
  className?: string;
  /** IntersectionObserver threshold. Default: 0.1 */
  threshold?: number;
}

const animationMap: Record<AnimationType, string> = {
  'fade-up': 'scroll-fade-up',
  'fade-in': 'scroll-fade-in',
  'slide-left': 'scroll-slide-left',
  'slide-right': 'scroll-slide-right',
};

export default function ScrollReveal({
  children,
  animation = 'fade-up',
  delay = 0,
  duration = 600,
  className = '',
  threshold = 0.1,
}: ScrollRevealProps) {
  const { ref, isVisible } = useScrollReveal({ threshold });
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Keep content visible before IO fires so automated a11y checks do not
  // measure zero-opacity text as a color-contrast failure.
  const style: React.CSSProperties = !hasMounted
    ? {}
    : isVisible
      ? {
          animation: `${animationMap[animation]} ${duration}ms ease-out both`,
          animationDelay: `${delay}ms`,
        }
      : { transform: animation === 'fade-up' ? 'translateY(24px)' : undefined };

  return (
    <div ref={ref as React.RefObject<HTMLDivElement>} className={`scroll-reveal ${className}`} style={style}>
      {children}
    </div>
  );
}
